import React, { useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { Dropdown } from 'react-bootstrap'
import queryString from 'query-string'
import { MenuPlaceholder } from 'src/components/Placeholders/MenuPlaceholder'
import { Spinner } from 'src/Throbber'
import { NONE_ID } from 'src/Utils/globalConstants'
import { ChevronSVG } from 'src/components/Icons/Basic'
import {
  SearchBar,
  SearchIcon,
  SearchInput,
  SearchInputContainer,
} from 'src/components/Reusable/Search/Search.styles'
import { useNavigateContext } from '../../../Navigation/NavigateContext'
import { FilterDropdownToggle } from '../FilterBar.styles'
import { DropDownMenu, NestedDropdownMenu } from './FilterDropdown.styles'
import { MultiSelectDropdownItems } from './FilterDropdown'
import { DropdownItemType } from './FilterDropdown.constants'
import { EnsureParent } from './FilterDropdown.types'

export const NestedFilter = ({
  filterParam,
  filterValues,
  ensureParent,
  hasSearch,
}: {
  filterParam: string
  filterValues: DropdownItemType[]
  ensureParent?: EnsureParent
  hasSearch?: boolean
}): JSX.Element => {
  const location = useLocation()
  const [activeSubmenu, setActiveSubmenu] = useState<string>(NONE_ID)
  const [searchQuery, setSearchQuery] = useState('')
  const searchParamsFallback = queryString.parse(location.search, {
    arrayFormat: 'bracket',
  })
  const {
    navigateState: {
      parsedQueryParams,
      locationSearchString: contextLocationSearchString,
    },
  } = useNavigateContext()
  const searchParams = parsedQueryParams || searchParamsFallback
  const locationSearchString = contextLocationSearchString ?? location.search

  const appliedFilters = useMemo(() => {
    const result: Record<string, string[]> = {}

    const preprocessed = searchParams[filterParam]
    result[filterParam] = Array.isArray(preprocessed)
      ? (preprocessed as string[])
      : preprocessed
      ? [preprocessed as string]
      : []

    // Include each item's nested filter param so MultiSelectDropdownItemComponent
    // can compute href correctly for level-3 items
    filterValues.forEach((item) => {
      const nestedParam = item.nestedFilter?.filterParam
      if (nestedParam) {
        const raw = searchParams[nestedParam]
        result[nestedParam] = Array.isArray(raw)
          ? (raw as string[])
          : raw
          ? [raw as string]
          : []
      }
    })

    // Derive virtual parent checked state: parent is "checked" only when ALL children selected
    filterValues.forEach((item) => {
      const nestedParam = item.nestedFilter?.filterParam
      const nestedItems = item.nestedFilter?.filterValues
      if (nestedParam && nestedItems && nestedItems.length > 0) {
        const nestedApplied = result[nestedParam] ?? []
        const allSelected = nestedItems.every((child) =>
          nestedApplied.includes(child.value),
        )
        if (allSelected) {
          result[filterParam] = [...(result[filterParam] ?? []), item.value]
        }
      }
    })

    return result
  }, [searchParams, filterParam, filterValues])

  // Partial: some (but not all) children selected → indeterminate checkbox
  const partialFilters = useMemo(() => {
    const result: Record<string, string[]> = {}
    filterValues.forEach((item) => {
      const nestedParam = item.nestedFilter?.filterParam
      const nestedItems = item.nestedFilter?.filterValues
      if (nestedParam && nestedItems && nestedItems.length > 0) {
        const raw = searchParams[nestedParam]
        const nestedApplied: string[] = Array.isArray(raw)
          ? (raw as string[])
          : raw
          ? [raw as string]
          : []
        const selectedCount = nestedItems.filter((child) =>
          nestedApplied.includes(child.value),
        ).length
        if (selectedCount > 0 && selectedCount < nestedItems.length) {
          result[filterParam] = [...(result[filterParam] ?? []), item.value]
        }
      }
    })
    return result
  }, [searchParams, filterParam, filterValues])

  const visibleValues = useMemo(() => {
    if (!hasSearch || !searchQuery) return filterValues
    const q = searchQuery.toLowerCase()
    return filterValues.filter(
      (item) =>
        typeof item.label === 'string' && item.label.toLowerCase().includes(q),
    )
  }, [filterValues, hasSearch, searchQuery])

  return (
    <NestedDropdownMenu>
      {hasSearch && (
        <SearchInputContainer style={{ padding: '8px 4px' }}>
          <SearchBar style={{ width: '100%' }}>
            <SearchIcon />
            <SearchInput
              placeholder="Search..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </SearchBar>
        </SearchInputContainer>
      )}
      <MultiSelectDropdownItems
        filterParam={filterParam}
        filterValues={visibleValues}
        appliedFilters={appliedFilters}
        location={location}
        searchParams={searchParams}
        activeSubmenu={activeSubmenu}
        setActiveSubmenu={setActiveSubmenu}
        locationSearchString={locationSearchString}
        ensureParent={ensureParent}
        partialFilters={partialFilters}
      />
    </NestedDropdownMenu>
  )
}

export const LoadingFilterDropdown = ({
  filterName,
  customName,
  isNested,
}: {
  filterName: string
  customName?: string
  isNested?: boolean
}): JSX.Element => (
  <>
    <Dropdown.Toggle
      as={FilterDropdownToggle}
      id={`${filterName}-filter-dropdown`}
      $isNested={isNested}
    >
      <p>{customName || filterName}</p>
      <Spinner size="s" color="primary" mx="xxs" />
    </Dropdown.Toggle>
    <DropDownMenu>
      <MenuPlaceholder height={108} />
    </DropDownMenu>
  </>
)

export const NestedFilterChevron = ({
  isOpen,
}: {
  isOpen?: boolean
}): JSX.Element => (
  <ChevronSVG
    width="12"
    height="12"
    fill="fill.primary.dark"
    style={{
      transform: `rotate(${isOpen ? '180deg' : '0'})`,
      margin: '8px',
    }}
  />
)
